import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Stack } from '@mui/material';
import { Description as JournalIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import WidgetCard from '../WidgetCard';
import { SiteVisitLogService } from '../../../services/SiteVisitLogService';
import { SiteVisitLog } from '../../../models/SiteVisitLog';

interface JournalWidgetProps {
  projectId: number;
  /**
   * 액션 버튼 클릭 시 부모가 처리할 콜백. 지정 시 페이지 이동 대신
   * 부모가 모달을 띄우는 등의 동작이 가능하다. 미지정 시 기존처럼
   * 일지 페이지로 navigate.
   */
  onActionClick?: () => void;
  /** 일지 행 클릭 시 상세 모달을 띄우는 콜백 (개요에서 바로 보기). */
  onItemClick?: (log: SiteVisitLog) => void;
}

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.substring(0, 10);
  return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
};

const JournalWidget: React.FC<JournalWidgetProps> = ({ projectId, onActionClick, onItemClick }) => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<SiteVisitLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    SiteVisitLogService.list(projectId)
      .then((data) => {
        if (!cancelled) setLogs(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : '조회 실패');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const recent = logs.slice(0, 3);

  return (
    <WidgetCard
      title={`최근 출장 일지 ${logs.length > 0 ? `(${logs.length}건)` : ''}`}
      icon={<JournalIcon fontSize="small" />}
      actionLabel="일지 전체"
      onAction={onActionClick ?? (() => navigate(`/project/${projectId}/journal`))}
      testId="widget-journal"
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : error ? (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      ) : recent.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          아직 출장 일지가 없습니다. "일지 전체"에서 첫 일지를 작성하세요.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {recent.map((log) => (
            <Box
              key={log.id}
              onClick={onItemClick ? () => onItemClick(log) : undefined}
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'baseline',
                ...(onItemClick && {
                  cursor: 'pointer',
                  borderRadius: 1,
                  mx: -0.5,
                  px: 0.5,
                  '&:hover': { bgcolor: 'action.hover' },
                }),
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48 }}>
                {formatDate(log.visited_at)}
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {log.purpose}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {log.visitor_name}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </WidgetCard>
  );
};

export default JournalWidget;
